import { Header } from '../components/Header.js';
import { Footer } from '../components/Footer.js';
import type { CurrencyCode } from '../hooks/useCurrency.js';
import './Legal.css';

/**
 * The DPA & sub-processor register (docs/landing/dpa-and-subprocessors.md), published as a real
 * page for the first time. The doc was counsel-cleared 2026-08-08 and marked "Yes — publish
 * as-is" in docs/landing/README.md's document table, but nothing in this app ever rendered it —
 * the same "specified but unbuilt" shape this repo keeps finding elsewhere. Content is hand-
 * transcribed here rather than rendered from markdown, matching Docs.tsx's existing pattern for
 * this app, so a change to the source doc must be mirrored here deliberately rather than by a
 * build step nobody wrote.
 */
export interface LegalPageProps {
  ccyCode: CurrencyCode;
  regionLabel: string;
  onCycleCurrency: () => void;
  onGoHome: () => void;
  onGoDocs: () => void;
  onGoConnectorsDocs: () => void;
  onGoPricing: () => void;
  onOpenAudit: () => void;
  onOpenBooking: () => void;
  onToggleHelp: () => void;
}

export function LegalPage({
  ccyCode,
  regionLabel,
  onCycleCurrency,
  onGoHome,
  onGoDocs,
  onGoConnectorsDocs,
  onGoPricing,
  onOpenAudit,
  onOpenBooking,
  onToggleHelp,
}: LegalPageProps) {
  return (
    <div className="land-legal">
      <Header
        view="legal"
        ccyCode={ccyCode}
        regionLabel={regionLabel}
        onCycleCurrency={onCycleCurrency}
        onGoHome={onGoHome}
        onGoDocs={onGoDocs}
        onOpenAudit={onOpenAudit}
        onOpenBooking={onOpenBooking}
        onToggleHelp={onToggleHelp}
      />
      <main className="land-legal__layout land-container">
        <article className="land-docs__article land-legal__article">
          <div className="land-docs__breadcrumb">
            <span>Legal</span>
            <span>/</span>
            <span className="land-docs__breadcrumb-current">Data Processing Agreement &amp; sub-processors</span>
          </div>
          <h1>Data Processing Agreement &amp; sub-processor register</h1>
          <p className="land-docs__intro">
            The customer-facing statement of what our Data Processing Agreement commits to, and the
            live register of who processes your data on our side of the connection.
          </p>
          <div className="land-docs__meta">
            <span className="land-docs__chip">reviewed 2026-08-08</span>
            <span className="land-docs__chip land-docs__chip--tint">counsel-cleared</span>
          </div>

          <div className="land-docs__callout">
            <span aria-hidden="true">✻</span>
            <div>
              This page states what our DPA says, in language a security reviewer can read without
              a lawyer present. It is not itself the executable contract — that is counsel's
              document, signed per customer. And Dedupr is pre-launch: where a commitment below
              depends on something not yet operating, it says so in place.
            </div>
          </div>

          <h2 id="roles">1 · The roles, in one paragraph</h2>
          <p>
            <strong>You are the controller. Dedupr is the processor.</strong> You connect your own
            systems — a CRM, a marketing or sales-engagement tool, a document store, or a plain file
            export — and Dedupr profiles the data, finds duplicates and quality problems, and — only
            after one of your people approves each change — writes corrections back or exports
            cleaned copies to storage you own. We do not decide the purposes for which your data is
            processed, we do not enrich it from third-party sources, and we do not use it to train
            any model (§5 below).
          </p>
          <div className="land-docs__warning">
            <strong>This deliberately does not name every system we connect to, and never will.</strong>{' '}
            The set grows — today it already spans dedicated connectors (Salesforce, HubSpot,
            Microsoft SharePoint &amp; OneDrive, Google Drive) and a generic OAuth/REST path covering
            dozens more — and a legal commitment that has to be re-signed every time a new connector
            ships is a commitment nobody ratifies in time. The current, maintained list is the{' '}
            <button className="land-btn land-btn--plain" onClick={onGoConnectorsDocs}>
              connectors directory
            </button>
            , not this page; what does not change as that list grows is the model below.
          </div>
          <div className="land-docs__warning">
            <strong>Whatever you connect is your system, not ours.</strong> None of it is a Dedupr
            sub-processor — §3 below is the list of parties who process data on <em>our</em> side of
            the connection. Every system you connect is the controller-side tool you already run,
            that you choose to grant Dedupr <strong>read-only</strong> access to at connect time,
            under the same model regardless of which system it is. Write access to any one of them is
            requested separately, per action type, the first time you approve an action of that
            type — never at connect, and never for all of them at once.
          </div>
          <p>
            Where a data subject contacts Dedupr directly, we do not act on their request; we refer
            them to you as controller and record that we did (§6).
          </p>

          <h2 id="where">2 · Where your data lives — two locations</h2>
          <p>
            Two different kinds of data sit in two different places by design. A customer who reads
            "EU-pinned" as covering both would be misled.
          </p>
          <table className="land-legal__table">
            <thead>
              <tr>
                <th></th>
                <th>Your records</th>
                <th>Your account</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>What it is</td>
                <td>
                  The data you connect, from whichever system — plus everything derived from it
                  (staged files, the duplicate graph, snapshots, golden records, your audit log)
                </td>
                <td>
                  Our record that you are a customer: organisation name, chosen region, plan and
                  lifecycle state, billing identifiers, an opaque user reference ↔ organisation +
                  role, SSO configuration, deployment records
                </td>
              </tr>
              <tr>
                <td>Where</td>
                <td>The region you choose — EU or US — and it never leaves it</td>
                <td>One database in the United Kingdom (London), shared across all customers</td>
              </tr>
              <tr>
                <td>Who processes it</td>
                <td>Cloudflare (compute, storage, queues); the AI providers you enable; Resend for notification email</td>
                <td>Supabase (hosting this database and the sign-in service); Stripe (billing identifiers)</td>
              </tr>
            </tbody>
          </table>
          <p>
            <strong>The account database holds no direct identifier.</strong> The link between a
            person and an organisation is an opaque reference; names and email addresses live in the
            sign-in service, not in these tables. This is enforced in our build pipeline, not by
            convention.
          </p>
          <p>
            <strong>EEA → UK.</strong> The United Kingdom holds an EU adequacy decision, so this is a
            permitted transfer. Our DPA additionally carries Standard Contractual Clauses with the UK
            Addendum as a standing fallback, in force from signature.
          </p>

          <h2 id="subprocessors">3 · Sub-processor register</h2>
          <p>
            Structured by location, per §2. This is the <strong>platform baseline</strong> — the set
            every customer has. Only the AI providers you actually enable appear on your own
            register; a BYO-key customer has no Dedupr-side AI sub-processing at all.
          </p>
          <table className="land-legal__table">
            <thead>
              <tr>
                <th>Sub-processor</th>
                <th>Purpose</th>
                <th>Where</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Cloudflare, Inc.</td>
                <td>Compute, object storage, per-customer databases, queues and orchestration</td>
                <td>Your chosen region</td>
              </tr>
              <tr>
                <td>Resend</td>
                <td>Transactional and notification email</td>
                <td>Provider's regional endpoint</td>
              </tr>
              <tr>
                <td>An RFC 3161 timestamping authority</td>
                <td>Independent countersigning of the daily audit chain-head hash</td>
                <td>Vendor's own location</td>
              </tr>
              <tr>
                <td>AI providers you enable</td>
                <td>Adjudicating ambiguous duplicate pairs, classifying documents, generating embeddings</td>
                <td>Per your configuration</td>
              </tr>
              <tr>
                <td>Supabase</td>
                <td>Hosting the account database and sign-in service</td>
                <td>United Kingdom</td>
              </tr>
              <tr>
                <td>Stripe</td>
                <td>Billing and payment processing</td>
                <td>Provider's own infrastructure</td>
              </tr>
            </tbody>
          </table>

          <h2 id="rights">4 · Your rights, and how to reach us</h2>
          <p>
            As controller, you remain responsible for responding to your data subjects' rights
            requests. We support you in doing so — see the full DPA text for the mechanics of an
            erasure or access request reaching us through you.
          </p>
          <p>
            Questions about this page, the DPA, or the sub-processor register:{' '}
            <a href="mailto:privacy@dedupr.cloud">privacy@dedupr.cloud</a>.
          </p>

          <div className="land-docs__callout">
            <span aria-hidden="true">✻</span>
            <div>
              This page summarises the DPA's customer-facing terms and is not exhaustive. The full
              text, including retention schedules, AI transparency detail, and the incident-response
              commitment it references, is available on request ahead of a signed agreement.
            </div>
          </div>
        </article>
      </main>
      <Footer onGoDocs={onGoDocs} onGoPricing={onGoPricing} />
    </div>
  );
}
